import { useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useFetch } from "@/lib/useFetch";
import PlanGate from "@/app/PlanGate";
import { PageHeader, Btn, EmptyState, Spinner } from "@/app/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { UserCog, Plus, ShieldCheck, Ban } from "lucide-react";

const PERM_LABELS = {
  view_jobs: "View jobs", clock_in_out: "Clock in/out", manage_customers: "Manage customers",
  manage_jobs: "Manage jobs", manage_quotes: "Manage quotes", manage_invoices: "Manage invoices",
  manage_expenses: "Manage expenses", view_reports: "View reports",
};

function EmployeesInner() {
  const { data, loading, refetch } = useFetch("/employees");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", permissions: ["view_jobs", "clock_in_out", "manage_customers", "manage_jobs"] });

  const togglePerm = (p) => setForm({ ...form, permissions: form.permissions.includes(p) ? form.permissions.filter((x) => x !== p) : [...form.permissions, p] });
  const invite = async () => {
    if (!form.name.trim() || !form.email.trim() || form.password.length < 6) return toast.error("Name, email and 6+ char password required");
    try {
      await api.post("/employees", form);
      toast.success("Employee invited"); setOpen(false); refetch();
      setForm({ name: "", email: "", password: "", permissions: ["view_jobs", "clock_in_out", "manage_customers", "manage_jobs"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleDisabled = async (emp) => { await api.put(`/employees/${emp.id}`, { disabled: !emp.disabled }); refetch(); };

  const items = data?.items || [];
  const allPerms = data?.all_permissions || Object.keys(PERM_LABELS);
  return (
    <div className="animate-fade-up">
      <PageHeader title="Employees" description="Invite your team — invite-only and Full plan."
        action={<Btn variant="accent" onClick={() => setOpen(true)} data-testid="invite-employee-button"><Plus size={16} /> Invite employee</Btn>} />
      {loading ? <Spinner /> : items.length === 0 ? (
        <EmptyState icon={UserCog} title="No employees yet" description="Invite team members and set exactly what they can access."
          action={<Btn variant="accent" onClick={() => setOpen(true)}><Plus size={16} /> Invite employee</Btn>} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {items.map((e) => (
            <div key={e.id} className="amden-card p-5" data-testid={`employee-card-${e.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-navy text-white grid place-items-center font-semibold">{e.name?.[0]}</div>
                  <div><p className="font-semibold text-navy">{e.name}</p><p className="text-sm text-slate-500">{e.email}</p></div>
                </div>
                <button onClick={() => toggleDisabled(e)} className={`text-xs px-2 py-1 rounded-lg ${e.disabled ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}>{e.disabled ? "Disabled" : "Active"}</button>
              </div>
              <div className="flex flex-wrap gap-1 mt-3">
                {e.permissions?.map((p) => <span key={p} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{PERM_LABELS[p] || p}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Invite employee</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div><label className="text-sm font-medium text-slate-700">Name *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="employee-name" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
            <div><label className="text-sm font-medium text-slate-700">Email *</label><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="employee-email" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
            <div><label className="text-sm font-medium text-slate-700">Temporary password *</label><input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="employee-password" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2" /></div>
            <div><p className="text-sm font-medium text-slate-700 mb-2">Permissions</p>
              <div className="grid grid-cols-2 gap-2">
                {allPerms.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                    <input type="checkbox" checked={form.permissions.includes(p)} onChange={() => togglePerm(p)} className="rounded border-slate-300" />{PERM_LABELS[p] || p}
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter><Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn><Btn variant="accent" onClick={invite} data-testid="employee-invite-save">Send invite</Btn></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Employees() { return <PlanGate feature="employees"><EmployeesInner /></PlanGate>; }
